function isHookSpec(x) {
    if (!x) {
        return false;
    }
    if (typeof x === "string") {
        return (x.startsWith(":") ||
            x.startsWith("@media ") ||
            x.startsWith("@container ") ||
            x.startsWith("@supports ") ||
            x.includes("&"));
    }
    if (typeof x === "object") {
        if ("or" in x && x.or instanceof Array) {
            return !x.or.some(xx => !isHookSpec(xx));
        }
        if ("and" in x && x.and instanceof Array) {
            return !x.and.some(xx => !isHookSpec(xx));
        }
    }
    return false;
}
/** @internal */
export function genericStringify(_, value) {
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number") {
        return `${value}`;
    }
    return null;
}
function hash(obj) {
    const jsonString = JSON.stringify(obj);
    let hashValue = 0;
    for (let i = 0; i < jsonString.length; i++) {
        const charCode = jsonString.charCodeAt(i);
        hashValue = (hashValue << 5) - hashValue + charCode;
        hashValue &= 0x7fffffff;
    }
    return hashValue.toString(36);
}
export function buildHooksSystem(stringify = genericStringify) {
    return function createHooks(config, options) {
        const fallbackKeyword = (options === null || options === void 0 ? void 0 : options.fallback) || "unset";
        const stringifyImpl = (propertyName, value) => {
            return typeof value === "string" && value.startsWith("var(")
                ? value
                : stringify(propertyName, value);
        };
        const hookId = (options === null || options === void 0 ? void 0 : options.hookNameToId) ||
            ((hookName) => {
                const specHash = hash(config[hookName]);
                return (options === null || options === void 0 ? void 0 : options.debug)
                    ? `${hookName.replace(/[^A-Za-z0-9-]/g, "_")}-${specHash}`
                    : specHash;
            });
        const hooks = Object.entries(config)
            .map(([name, definition]) => {
            function nest(input) {
                if (typeof input === "object") {
                    if ("and" in input) {
                        if (input.and.length > 2) {
                            const [left, ...rest] = input.and;
                            return { and: [left, nest({ and: rest })] };
                        }
                        return {
                            and: input.and.map(item => typeof item === "string" ? item : nest(item)),
                        };
                    }
                    if ("or" in input) {
                        if (input.or.length > 2) {
                            const [left, ...rest] = input.or;
                            return { or: [left, nest({ or: rest })] };
                        }
                        return {
                            or: input.or.map(item => typeof item === "string" ? item : nest(item)),
                        };
                    }
                }
                return input;
            }
            if (!isHookSpec(definition) || typeof definition !== "object") {
                return [name, definition];
            }
            return [name, nest(definition)];
        })
            .flatMap(([name, definition]) => (function hooksCSS(name, definition) {
            if (!isHookSpec(definition)) {
                return [];
            }
            if (typeof definition === "object") {
                let a, operator, b, extraHooksCSS = [];
                if ("or" in definition) {
                    operator = "or";
                    [a, b] = definition.or;
                    if (!a) {
                        return [];
                    }
                    if (!b) {
                        return hooksCSS(name, a);
                    }
                    extraHooksCSS = [
                        {
                            init: (function aorb(x) {
                                const a = `${x}A`;
                                const b = `${x}B`;
                                return [
                                    `--${x}-0:var(--${a}-0,var(--${b}-0));`,
                                    `--${x}-1:var(--${a}-1) var(--${b}-1);`,
                                ].join("");
                            })(name),
                        },
                    ];
                }
                else if ("and" in definition) {
                    operator = "and";
                    [a, b] = definition.and;
                    if (!a) {
                        return [];
                    }
                    if (!b) {
                        return hooksCSS(name, a);
                    }
                    extraHooksCSS = [
                        {
                            init: (function aandb(x) {
                                const a = `${x}A`;
                                const b = `${x}B`;
                                return [
                                    `--${x}-0:var(--${a}-0) var(--${b}-0);`,
                                    `--${x}-1:var(--${a}-1,var(--${b}-1));`,
                                ].join("");
                            })(name),
                        },
                    ];
                }
                if (operator) {
                    return [
                        ...hooksCSS(`${name}A`, a),
                        ...hooksCSS(`${name}B`, b),
                        ...extraHooksCSS,
                    ];
                }
            }
            const init = `--${name}-0:initial;--${name}-1: ;`;
            let rule;
            if (typeof definition === "string") {
                if (definition.includes("&")) {
                    rule = `${definition.replace(/&/g, "*")}{--${name}-0: ;--${name}-1:initial;}`;
                }
                else if (definition.startsWith(":")) {
                    rule = `${definition}{--${name}-0: ;--${name}-1:initial;}`;
                }
                else if (definition.startsWith("@")) {
                    rule = `${definition}{*{--${name}-0: ;--${name}-1:initial;}}`;
                }
            }
            return rule === undefined ? [] : [{ init, rule }];
        })(hookId(name), definition))
            .reduce((acc, { init = "", rule = "" }) => ({
            init: acc.init + init,
            rule: acc.rule + rule,
        }), {
            init: "",
            rule: "",
        });
        function cssImpl(properties, fallback = propertyName => stringifyImpl(propertyName, properties[propertyName])) {
            const sort = options === null || options === void 0 ? void 0 : options.sort;
            const keys = sort ? Object.keys(properties) : [];
            for (const k in properties) {
                const key = k;
                const value = properties[key];
                if (key in config) {
                    const hookName = key;
                    const innerProperties = value;
                    cssImpl(innerProperties, propertyName => {
                        let v = stringifyImpl(propertyName, innerProperties[propertyName]);
                        if (v === null) {
                            v = fallback(propertyName);
                        }
                        if (v === null) {
                            v = fallbackKeyword;
                        }
                        return v;
                    });
                    for (const propertyNameStr in innerProperties) {
                        if (keys.indexOf(propertyNameStr) > keys.indexOf(hookName)) {
                            continue;
                        }
                        const propertyName = propertyNameStr;
                        const v1 = stringifyImpl(propertyName, innerProperties[propertyName]);
                        if (v1 !== null) {
                            let v0 = fallback(propertyName);
                            if (v0 === null) {
                                v0 = fallbackKeyword;
                            }
                            if (sort) {
                                delete properties[propertyName];
                            }
                            properties[propertyName] = `var(--${hookId(hookName)}-1, ${v1}) var(--${hookId(hookName)}-0, ${v0})`;
                        }
                    }
                    delete properties[hookName];
                }
                else if (sort) {
                    delete properties[key];
                    Object.assign(properties, { [key]: value });
                }
            }
            return properties;
        }
        function css(...properties) {
            const styles = [{}].concat(JSON.parse(JSON.stringify(properties)));
            do {
                const current = styles[0];
                const next = styles[1] || {};
                if (options === null || options === void 0 ? void 0 : options.sort) {
                    for (const k in next) {
                        if (k in current) {
                            delete current[k];
                        }
                    }
                }
                styles[0] = cssImpl(Object.assign(current, next));
                styles.splice(1, 1);
            } while (styles[1]);
            return styles[0];
        }
        return [`*{${hooks.init}}${hooks.rule}`, css];
    };
}
/**
 * A list of hooks offered as a "sensible default" to solve the most common use cases.
 *
 * @deprecated Use the `@css-hooks/recommended` package instead.
 */
export const recommended = {
    active: ":active",
    autofill: { or: [":autofill", ":-webkit-autofill"] },
    checked: ":checked",
    default: ":default",
    disabled: ":disabled",
    empty: ":empty",
    enabled: ":enabled",
    evenChild: ":nth-child(even)",
    firstChild: ":first-child",
    firstOfType: ":first-of-type",
    focus: ":focus",
    focusVisible: ":focus-visible",
    focusWithin: ":focus-within",
    hover: ":hover",
    inRange: ":in-range",
    indeterminate: ":indeterminate",
    invalid: ":invalid",
    lastChild: ":last-child",
    lastOfType: ":last-of-type",
    oddChild: ":nth-child(odd)",
    onlyChild: ":only-child",
    onlyOfType: ":only-of-type",
    outOfRange: ":out-of-range",
    placeholderShown: { or: [":placeholder-shown", ":-moz-placeholder-shown"] },
    readOnly: { or: [":read-only", ":-moz-read-only"] },
    readWrite: { or: [":read-write", ":-moz-read-write"] },
    required: ":required",
    target: ":target",
    valid: ":valid",
    visited: ":visited",
};
